/**
 * ポータル: 陸上勤怠（簡易出退勤）
 * 自分の打刻・月次表と、管理者向けの全員分の一覧・修正・CSV出力。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Download, Pencil, Plus } from 'lucide-react';
import * as api from '../../services/api';
import { Modal, Tabs, fieldStyle, labelStyle, fmtMinutes, fileUrl } from './ui';

interface Row {
  id: string; user_id: string; user_name: string | null; work_date: string;
  clock_in: string | null; clock_out: string | null; break_minutes: number;
  note: string | null; work_minutes: number | null; edited_by_name?: string | null;
}

const thisMonth = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 7);

export default function PortalOfficePage() {
  const user = useMemo(() => { try { return JSON.parse(localStorage.getItem('admin_user') ?? '{}'); } catch { return {}; } }, []);
  const isAdmin = ['system_admin', 'shore_admin'].includes(user?.user_role ?? '');

  const [tab, setTab] = useState('me');
  const [month, setMonth] = useState(thisMonth());
  const [myData, setMyData] = useState<{ rows: Row[]; total_minutes: number; days: number } | null>(null);
  const [allData, setAllData] = useState<{ rows: Row[]; totals: Array<{ user_id: string; user_name: string | null; days: number; total_minutes: number }> } | null>(null);
  const [today, setToday] = useState<Row | null>(null);
  const [edit, setEdit] = useState<Row | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [msg, setMsg] = useState('');

  const loadMe = useCallback(() => {
    api.get<{ data: { rows: Row[]; total_minutes: number; days: number } }>('/portal/office/me', { month }).then(r => setMyData(r.data)).catch(() => { /* noop */ });
    api.get<{ data: Row | null }>('/portal/office/today').then(r => setToday(r.data)).catch(() => { /* noop */ });
  }, [month]);
  const loadAll = useCallback(() => {
    if (isAdmin) api.get<{ data: { rows: Row[]; totals: Array<{ user_id: string; user_name: string | null; days: number; total_minutes: number }> } }>('/portal/office', { month }).then(r => setAllData(r.data)).catch(() => { /* noop */ });
  }, [isAdmin, month]);
  useEffect(() => { loadMe(); loadAll(); }, [loadMe, loadAll]);

  const punch = async (type: 'in' | 'out') => {
    setMsg('');
    try {
      const r = await api.post<{ data: Row }>('/portal/office/punch', { type });
      setToday(r.data);
      setMsg(type === 'in' ? `出勤を打刻しました（${r.data.clock_in}）` : `退勤を打刻しました（${r.data.clock_out}）`);
      loadMe(); loadAll();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : '打刻に失敗しました'); }
  };

  const table = (rows: Row[], showUser: boolean, editable: boolean) => (
    <div className="card" style={{ padding: 0, overflow: 'auto' }}>
      {rows.length === 0 ? <div className="empty" style={{ padding: 30 }}>記録はまだありません</div> : (
        <table>
          <thead><tr>
            <th style={{ width: 100 }}>日付</th>
            {showUser && <th style={{ width: 130 }}>氏名</th>}
            <th style={{ width: 80 }}>出勤</th><th style={{ width: 80 }}>退勤</th>
            <th style={{ width: 80 }}>休憩</th><th style={{ width: 80 }}>実働</th>
            <th>メモ</th>{editable && <th style={{ width: 60 }} />}
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ fontSize: 12.5 }}>{r.work_date.replace(/-/g, '/')}</td>
                {showUser && <td style={{ fontSize: 12.5 }}>{r.user_name}</td>}
                <td>{r.clock_in ?? '—'}</td>
                <td>{r.clock_out ?? '—'}</td>
                <td style={{ fontSize: 12.5 }}>{r.break_minutes}分</td>
                <td style={{ fontWeight: 600 }}>{fmtMinutes(r.work_minutes)}</td>
                <td style={{ fontSize: 12.5, color: '#6b7280' }}>
                  {r.note}{r.edited_by_name && <span style={{ color: '#b45309' }}>（修正: {r.edited_by_name}）</span>}
                </td>
                {editable && (
                  <td>
                    <button className="btn" style={{ padding: '4px 8px' }} onClick={() => setEdit(r)}><Pencil size={13} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={20} /> 陸上勤怠
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {msg && <span style={{ fontSize: 12.5, color: '#0f766e', fontWeight: 600 }}>{msg}</span>}
          <button className="btn-p" disabled={!!today?.clock_in} onClick={() => void punch('in')}>出勤</button>
          <button className="btn-p" style={{ background: '#d97706' }} disabled={!today?.clock_in || !!today?.clock_out} onClick={() => void punch('out')}>退勤</button>
        </div>
      </div>

      <div className="card" style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Tabs active={tab} onChange={setTab} tabs={[
          { key: 'me', label: '自分の勤怠' },
          ...(isAdmin ? [{ key: 'all', label: '全員の勤怠' }] : []),
        ]} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="month" style={{ ...fieldStyle, width: 150 }} value={month} onChange={e => setMonth(e.target.value)} />
          {isAdmin && tab === 'all' && (
            <>
              <button className="btn" style={{ display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setShowAdd(true)}>
                <Plus size={13} /> 行を追加
              </button>
              <a className="btn" style={{ display: 'flex', alignItems: 'center', gap: 5, textDecoration: 'none' }}
                href={fileUrl(`/portal/office/export.csv?month=${month}`)}>
                <Download size={13} /> CSV
              </a>
            </>
          )}
        </div>
      </div>

      {tab === 'me' && myData && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
            <div className="card" style={{ padding: '12px 20px', flex: 'none' }}>
              <div style={{ fontSize: 11.5, color: '#6b7280' }}>出勤日数（{month.replace('-', '/')}）</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{myData.days}日</div>
            </div>
            <div className="card" style={{ padding: '12px 20px', flex: 'none' }}>
              <div style={{ fontSize: 11.5, color: '#6b7280' }}>実働合計</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{fmtMinutes(myData.total_minutes)}</div>
            </div>
          </div>
          {table(myData.rows, false, true)}
        </>
      )}

      {tab === 'all' && isAdmin && allData && (
        <>
          <div className="card" style={{ padding: '10px 16px', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {allData.totals.map(t => (
              <span key={t.user_id} style={{ fontSize: 12.5, color: '#374151' }}>
                <strong>{t.user_name}</strong> {t.days}日 / {fmtMinutes(t.total_minutes)}
              </span>
            ))}
          </div>
          {table(allData.rows, true, true)}
        </>
      )}

      {edit && (
        <EditModal row={edit} onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); loadMe(); loadAll(); }} />
      )}
      {showAdd && (
        <AddModal onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); loadAll(); }} />
      )}
    </div>
  );
}

function EditModal({ row, onClose, onSaved }: { row: Row; onClose: () => void; onSaved: () => void }) {
  const [clockIn, setClockIn] = useState(row.clock_in ?? '');
  const [clockOut, setClockOut] = useState(row.clock_out ?? '');
  const [breakMin, setBreakMin] = useState(String(row.break_minutes));
  const [note, setNote] = useState(row.note ?? '');
  const [busy, setBusy] = useState(false);
  return (
    <Modal width={440} title={`${row.work_date.replace(/-/g, '/')} の勤怠を修正`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>出勤</label>
            <input type="time" style={fieldStyle} value={clockIn} onChange={e => setClockIn(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>退勤</label>
            <input type="time" style={fieldStyle} value={clockOut} onChange={e => setClockOut(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>休憩（分）</label>
            <input type="number" style={fieldStyle} value={breakMin} onChange={e => setBreakMin(e.target.value)} />
          </div>
        </div>
        <div>
          <label style={labelStyle}>メモ</label>
          <input style={fieldStyle} value={note} onChange={e => setNote(e.target.value)} placeholder="例: 打刻忘れのため修正" />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={async () => {
            setBusy(true);
            try {
              await api.put(`/portal/office/${row.id}`, {
                clock_in: clockIn || null, clock_out: clockOut || null,
                break_minutes: parseInt(breakMin, 10) || 0, note: note || null,
              });
              onSaved();
            } catch (e: unknown) { alert(e instanceof Error ? e.message : '保存に失敗しました'); }
            finally { setBusy(false); }
          }}>{busy ? '保存中...' : '保存する'}</button>
        </div>
      </div>
    </Modal>
  );
}

function AddModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [users, setUsers] = useState<Array<{ id: string; user_name: string }>>([]);
  const [userId, setUserId] = useState('');
  const [date, setDate] = useState('');
  const [clockIn, setClockIn] = useState('09:00');
  const [clockOut, setClockOut] = useState('17:30');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get<{ data: Array<{ id: string; user_name: string }> }>('/portal/chat/directory').then(r => setUsers(r.data ?? [])).catch(() => { /* noop */ });
  }, []);
  return (
    <Modal width={440} title="勤怠の行を追加（入れ忘れ用）" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={labelStyle}>対象者</label>
          <select style={fieldStyle} value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">選択してください</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.user_name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1.2 }}>
            <label style={labelStyle}>日付</label>
            <input type="date" style={fieldStyle} value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>出勤</label>
            <input type="time" style={fieldStyle} value={clockIn} onChange={e => setClockIn(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>退勤</label>
            <input type="time" style={fieldStyle} value={clockOut} onChange={e => setClockOut(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onClose}>キャンセル</button>
          <button className="btn-p" disabled={busy} onClick={async () => {
            if (!userId || !date) { alert('対象者と日付を選んでください'); return; }
            setBusy(true);
            try {
              await api.post('/portal/office', { user_id: userId, work_date: date, clock_in: clockIn || null, clock_out: clockOut || null });
              onSaved();
            } catch (e: unknown) { alert(e instanceof Error ? e.message : '追加に失敗しました'); }
            finally { setBusy(false); }
          }}>{busy ? '追加中...' : '追加する'}</button>
        </div>
      </div>
    </Modal>
  );
}
